(function () {
  function toNumber(value) {
    return parseInt(value, 10) || 0;
  }

  function extractDateFromText(value) {
    if (!value || value === "" || value === "-" || value === "false") return null;

    const raw = String(value).trim();
    const match = raw.match(/(\d{1,4}[/-]\d{1,2}[/-]\d{1,4})/);
    if (!match) return null;

    const parts = match[1].split(/[/-]/).map((part) => parseInt(part, 10));
    if (parts.length !== 3 || parts.some((part) => isNaN(part))) return null;

    let month;
    let day;
    let year;

    if (parts[0] > 31) {
      year = parts[0];
      month = parts[1];
      day = parts[2];
    } else if (parts[2] > 31) {
      month = parts[0];
      day = parts[1];
      year = parts[2];
    } else {
      return null;
    }

    if (year < 100 || month < 1 || month > 12 || day < 1 || day > 31) return null;

    // Keep parsing permissive for vendor metafield inputs that can contain non-standard dates.
    return new Date(year, month - 1, day);
  }

  function normalizeBackorderDate(rawBackorder) {
    const date = extractDateFromText(rawBackorder);
    if (!date) return "";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const normalized = new Date(date.getTime());
    normalized.setHours(0, 0, 0, 0);
    if (normalized < today) return "";

    return (
      (normalized.getMonth() + 1).toString().padStart(2, "0") +
      "/" +
      normalized.getDate().toString().padStart(2, "0") +
      "/" +
      normalized.getFullYear()
    );
  }

  function hasBackorderState(rawBackorder, normalizedBackorder, stockNotes) {
    if (normalizedBackorder) return true;
    if (rawBackorder && rawBackorder !== "-" && extractDateFromText(rawBackorder)) return true;
    if (stockNotes && /backorder/i.test(stockNotes)) return true;
    return false;
  }

  function formatRange(begin, end) {
    const useWeeks = end > 14;
    const rangeStart = useWeeks ? Math.floor(begin / 7) : begin;
    const rangeEnd = useWeeks ? Math.ceil(end / 7) : end;
    const rangeUnit = useWeeks ? "weeks" : rangeEnd === 1 ? "business day" : "business days";
    return rangeStart === rangeEnd ? `${rangeEnd} ${rangeUnit}` : `${rangeStart}-${rangeEnd} ${rangeUnit}`;
  }

  function formatFutureDateRange(date) {
    if (!date) return "";

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const target = new Date(date.getTime());
    target.setHours(0, 0, 0, 0);

    const msPerDay = 24 * 60 * 60 * 1000;
    const daysUntil = Math.ceil((target.getTime() - today.getTime()) / msPerDay);
    if (daysUntil <= 0) return "";

    if (daysUntil <= 14) {
      return `${daysUntil} ${daysUntil === 1 ? "business day" : "business days"}`;
    }

    const weeksStart = Math.floor(daysUntil / 7);
    const weeksEnd = Math.ceil(daysUntil / 7);
    return weeksStart === weeksEnd ? `${weeksEnd} weeks` : `${weeksStart}-${weeksEnd} weeks`;
  }

  function isWarehouseStock(config) {
    if (typeof config.warehouseStock !== "undefined") return !!config.warehouseStock;

    return (
      config.in_stock_newyork === "true" ||
      config.in_stock_newyork === true ||
      config.in_stock_florida === "true" ||
      config.in_stock_florida === true ||
      config.in_stock_utah === "true" ||
      config.in_stock_utah === true
    );
  }

  function normalizePunctuationSpacing(value) {
    return String(value || "")
      .replace(/\s+([:.])/g, "$1")
      .replace(/([:.])\s+/g, "$1 ")
      .trim();
  }

  function normalizeBackorderMessage(value) {
    const text = String(value || "").trim();
    if (!text) return "";
    if (!/backorder|pre-order/i.test(text)) return text;
    return normalizePunctuationSpacing(text);
  }

  function buildMessage(input) {
    if (!input) return {html: "Please make a selection.", text: ""};

    const begin = toNumber(input.shipDateBegin || input.ship_date_begin);
    const end = toNumber(input.shipDateEnd || input.ship_date_end);
    const stockNotes = input.stockNotes || input.stock_notes || "";
    const rawBackorder =
      (input.rawBackorder || input.backorderDate || input.backorder_date || "") === "-"
        ? ""
        : input.rawBackorder || input.backorderDate || input.backorder_date || "";
    const hasBackorderValue = !!String(rawBackorder).trim();
    const extractedBackorderDate = extractDateFromText(rawBackorder);
    const normalizedBackorder = normalizeBackorderDate(rawBackorder);
    const isBackorder = hasBackorderState(rawBackorder, normalizedBackorder, stockNotes);
    const hasPastBackorderDate = !!(
      extractedBackorderDate && extractedBackorderDate.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
    );
    const preOrder = !!(input.preOrder || input.pre_order || input.isPreOrder);
    const isAvailable =
      input.available === true || input.available === "true" || input.isAvailable === true || input.isAvailable === "true";
    const warehouseStock = isWarehouseStock(input);

    if (warehouseStock) {
      return {
        html: `<span class="pdp-in-stock">In Stock.</span> Ships in 5-7 business days.`,
        text: "In Stock. Ships in 5-7 business days.",
      };
    }

    if (hasBackorderValue || isBackorder) {
      if (hasPastBackorderDate) {
        return {
          html: `<span class="backorder-date"><strong>Backordered</strong>.</span>`,
          text: "Backordered.",
        };
      }

      const label = preOrder ? "Available for pre-order" : "Backordered";
      const labelHtml = label === "Backordered" ? `<strong>${label}</strong>` : label;

      if (normalizedBackorder) {
        const msg = normalizeBackorderMessage(`${label}. Scheduled to ship ${normalizedBackorder}.`);
        return {
          html: `<span class="backorder-date">${msg.replace(label, labelHtml)}</span>`,
          text: msg,
        };
      }

      if (hasBackorderValue) {
        const backorderText = normalizeBackorderMessage(String(rawBackorder).trim());
        const msg = `${label}. Scheduled to ship ${backorderText}.`;
        return {
          html: `<span class="backorder-date">${labelHtml}. Scheduled to ship ${backorderText}.</span>`,
          text: msg,
        };
      }

      if (stockNotes) {
        const note = normalizeBackorderMessage(stockNotes);
        const msg = note;
        return {
          html: `<span class="backorder-date">${note}</span>`,
          text: msg,
        };
      }

      if (begin && end) {
        const msg = normalizeBackorderMessage(`${label}: Usually ships in ${formatRange(begin, end)}.`);
        return {
          html: `<span class="backorder-date">${labelHtml}: Usually ships in ${formatRange(begin, end)}.</span>`,
          text: msg,
        };
      }

      const msg = normalizeBackorderMessage(stockNotes || `${label}.`);
      const bodyHtml = msg.replace(label, labelHtml);
      return {
        html: `<span class="backorder-date">${bodyHtml}</span>`,
        text: msg,
      };
    }

    if (stockNotes) {
      const msg = stockNotes;
      return {
        html: `<span class="stock-note">${stockNotes}</span>`,
        text: msg,
      };
    }

    if (!isAvailable && !hasBackorderValue) {
      return {
        html: `<span class="backorder-date"><strong>Backordered</strong></span>`,
        text: "Backordered",
      };
    }

    if (!begin && !end) return {html: "", text: ""};

    if (isAvailable) {
      const range = formatRange(begin, end);
      const msg = `Ships in ${range}.`;
      return {html: msg, text: msg};
    }

    const msg = `Usually ships in ${formatRange(begin, end)}.`;
    return {html: msg, text: msg};
  }

  function formatText(input) {
    return buildMessage(input).text;
  }

  window.ShippingEstimate = {
    extractDateFromText,
    normalizeBackorderDate,
    hasBackorderState,
    formatRange,
    formatFutureDateRange,
    buildMessage,
    formatText,
  };
})();
